// Paperwork-check affordances for the Ops-reviewer salesIntake variant.
// Derives a lightweight checklist from the intake row snapshot — no rules
// engine; post-acceptance paperwork stays on Ops → operations.

import type { OpsOrderIntakeRow } from "@/lib/orderIntakeClient";
import { extractIntakeReviewParties } from "@/lib/ops/intakeReviewParties";

export type IntakePaperworkCheckStatus = "pass" | "warn" | "fail";

export type IntakePaperworkCheckItem = {
  id: string;
  label: string;
  status: IntakePaperworkCheckStatus;
  detail?: string;
};

export type IntakeCustomerContactSnapshot = {
  name: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
};

export function readIntakeCustomerContact(
  row: OpsOrderIntakeRow,
): IntakeCustomerContactSnapshot | null {
  const parties = extractIntakeReviewParties(row);
  const c = parties.customer;
  if (!c) return null;
  const address = parties.addresses[0]?.lines.join(", ") ?? null;
  if (!c.name && !c.phone && !c.email && !address) return null;
  return { name: c.name, phone: c.phone, email: c.email, address };
}

export function buildIntakePaperworkChecks(row: OpsOrderIntakeRow): IntakePaperworkCheckItem[] {
  const checks: IntakePaperworkCheckItem[] = [];
  const contact = readIntakeCustomerContact(row);

  if (row.kind === "customer") {
    const hasContactId = Boolean(row.proposed_end_customer_contact_id?.trim());
    const hasContactFields = Boolean(contact?.name);
    const hasReach =
      Boolean(contact?.phone?.trim()) || Boolean(contact?.email?.trim());
    checks.push({
      id: "customer_contact",
      label: "Customer contact captured",
      status: hasContactFields && hasReach ? "pass" : hasContactId ? "warn" : "fail",
      detail:
        hasContactFields && hasReach
          ? [contact?.name, contact?.phone, contact?.email].filter(Boolean).join(" · ")
          : hasContactId
            ? `Contact id ${row.proposed_end_customer_contact_id} — name/phone/email not in payload`
            : "Missing end-customer contact",
    });

    checks.push({
      id: "sale_path",
      label: "Sale path specified",
      status: row.proposed_sale_path ? "pass" : "fail",
      detail: row.proposed_sale_path ?? undefined,
    });
  }

  checks.push({
    id: "seller_location",
    label: "Seller location pinned",
    status: row.proposed_seller_location_id ? "pass" : "warn",
    detail: row.proposed_seller_location_id ?? "Optional for some sources; required for dealer submissions",
  });

  checks.push({
    id: "plant",
    label: "Proposed plant (for accept)",
    status: row.proposed_plant_id ? "pass" : "warn",
    detail: row.proposed_plant_id
      ? row.proposed_plant_id
      : "Accept may fail until a plant is set — Mfg accepter can override at accept time",
  });

  const notes =
    typeof row.raw_payload?.notes === "string" && row.raw_payload.notes.trim()
      ? row.raw_payload.notes.trim()
      : null;
  checks.push({
    id: "submitter_notes",
    label: "Submitter notes reviewed",
    status: notes ? "pass" : "warn",
    detail: notes ?? "No notes on this submission",
  });

  return checks;
}

export function intakePaperworkReadyForAccept(checks: IntakePaperworkCheckItem[]): boolean {
  return !checks.some((c) => c.status === "fail");
}
